/* Display conversions never modify Shopify option values or variant IDs. */
(() => {
  if (customElements.get('monateo-size-picker')) return;
  const normalize = value => String(value).trim().toUpperCase().replace(/[–—]/g, '-').replace(/\s*-\s*/g, '-').replace(/\s+/g, ' ');
  function parseChart(text) {
    const seen = new Set();
    return String(text || '').split(/\r?\n/).map(line => {
      const [label, US, EU, UK] = line.split('|').map(cell => cell.trim());
      return { label, US, EU, UK };
    }).filter(row => {
      if (!row.label || !row.US || !row.EU || !row.UK || !/^\d+(?:\.\d+)?(?:\s*[-–—]\s*\d+(?:\.\d+)?)?$/.test(row.EU) || row.EU.split(/[-–—]/).some(value => Number(value) <= 0) || seen.has(normalize(row.label))) return false;
      seen.add(normalize(row.label));
      return true;
    });
  }
  function findRow(value, rows) {
    const original = normalize(value);
    // Exact label takes priority (UK S must not override the size label S).
    const exact = rows.find(row => normalize(row.label) === original);
    if (exact) return exact;
    const base = original.split('(')[0].trim();
    const named = rows.find(row => normalize(row.label) === base);
    if (named) return named;
    const matches = rows.filter(row => ['US','EU','UK'].some(region => original === normalize(row[region]) || original === region + ' ' + normalize(row[region])));
    return matches.length === 1 ? matches[0] : null;
  }
  class MonateoSizePicker extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => { if (this.isConnected) this.initialize(); });
    }
    initialize() {
      this.abort?.abort();
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      document.addEventListener('keydown', event => {
        if (event.key !== 'Tab') return;
        this.keyboardOpen = true;
        this.querySelectorAll('[data-guide-pointer-focus]').forEach(node => node.removeAttribute('data-guide-pointer-focus'));
        this.opener?.removeAttribute('data-guide-pointer-focus');
      }, options);
      this.region = ['US','EU','UK'].includes(this.dataset.region) ? this.dataset.region : 'EU';
      this.unit = 'mm';
      this.rows = [];
      const chart = this.querySelector('[data-size-chart]');
      try { this.rows = parseChart(chart ? JSON.parse(chart.textContent) : ''); } catch (_) { /* Keep original labels if configuration is invalid. */ }
      this.labels = [...this.querySelectorAll('[data-ring-label]')];
      this.mapped = this.labels.map(label => ({ label, row: findRow(label.dataset.originalSize, this.rows) }));
      this.modal = this.querySelector('[data-size-guide-modal]');
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button || !this.contains(button)) return;
        if (button.hasAttribute('data-size-region')) this.setRegion(button.dataset.sizeRegion);
        if (button.hasAttribute('data-size-unit')) { this.unit = button.dataset.sizeUnit; this.render(); }
        if (button.hasAttribute('data-size-guide-open')) this.open(button, event.detail === 0);
        if (button.hasAttribute('data-size-guide-close')) this.modal?.close();
      }, options);
      this.addEventListener('change', event => {
        if (event.target.matches('[data-guide-region]')) this.setRegion(event.target.value);
        if (event.target.matches('input[type="radio"]')) this.highlight();
      }, options);
      if (this.modal) {
        this.modal.addEventListener('click', event => { if (event.target === this.modal) this.modal.close(); }, options);
        this.modal.addEventListener('close', () => this.restore(), options);
      }
      this.querySelectorAll('[data-region-controls]').forEach(control => { control.hidden = false; });
      this.render();
    }
    disconnectedCallback() {
      this.abort?.abort();
      if (this.modal?.open) this.modal.close();
      this.restore();
    }
    setRegion(region) {
      if (!['US','EU','UK'].includes(region)) return;
      const changed = this.region !== region;
      this.region = region;
      this.render();
      if (changed && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.labels.forEach(label => {
          label.getAnimations().forEach(animation => animation.cancel());
          label.animate([{ opacity: 0.35 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' });
        });
      }
    }
    render() {
      this.mapped.forEach(({label, row}) => {
        if (!row) { label.textContent = label.dataset.originalSize; return; }
        const named = normalize(label.dataset.originalSize).split('(')[0].trim() === normalize(row.label) && /[a-z]/i.test(row.label) && !/^(US|EU|UK)\s/i.test(row.label);
        const display = region => named ? `${row.label} (${row[region]})` : row[region];
        // All regional strings participate in layout; only the current one is visible/read aloud.
        label.replaceChildren();
        ['EU', 'US', 'UK'].forEach(region => {
          const span = document.createElement('span');
          span.textContent = display(region);
          if (region !== this.region) { span.className = 'monateo-size-reserve'; span.setAttribute('aria-hidden', 'true'); }
          label.append(span);
        });
      });
      this.querySelectorAll('[data-size-region]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.sizeRegion === this.region)));
      this.querySelectorAll('[data-size-unit]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.sizeUnit === this.unit)));
      const select = this.querySelector('[data-guide-region]');
      if (select) select.value = this.region;
      const heading = this.querySelector('[data-guide-region-heading]');
      if (heading) heading.textContent = this.region;
      this.querySelectorAll('[data-guide-unit]').forEach(node => { node.textContent = this.unit; });
      const body = this.querySelector('[data-guide-rows]');
      if (!body) return;
      // Only show sizes actually offered for this product, including sold-out sizes.
      const offered = new Set(this.mapped.map(item => item.row).filter(Boolean));
      const rows = this.rows.filter(row => offered.has(row));
      body.replaceChildren();
      rows.forEach(row => {
        const tr = document.createElement('tr');
        tr.dataset.chartSize = row.label;
        const circumferences = row.EU.split(/[-–—]/).map(Number);
        const divisor = this.unit === 'in' ? 25.4 : 1;
        const numbers = [Math.PI, 1].map((factor, index) => circumferences.map(value => (value / factor / divisor).toFixed(this.unit === 'in' ? 2 : index === 0 ? 1 : 0)).join('–'));
        [row.label, row[this.region], ...numbers].forEach((value, index) => {
          const cell = document.createElement(index === 0 ? 'th' : 'td');
          if (index === 0) cell.scope = 'row';
          cell.textContent = value;
          tr.append(cell);
        });
        body.append(tr);
      });
      this.querySelector('[data-guide-empty]').hidden = rows.length > 0;
      this.highlight();
    }
    highlight() {
      const selected = this.querySelector('[data-ring-options] input:checked');
      const row = selected ? findRow(selected.value, this.rows) : null;
      this.querySelectorAll('[data-chart-size]').forEach(tr => {
        const active = tr.dataset.chartSize === row?.label;
        tr.classList.toggle('is-selected', active);
        if (active) tr.setAttribute('aria-current', 'true'); else tr.removeAttribute('aria-current');
      });
    }
    open(button, keyboard = true) {
      if (!this.modal || this.modal.open) return;
      this.opener = button;
      this.keyboardOpen = keyboard;
      this.modal.querySelector('[data-size-guide-close]')?.toggleAttribute('data-guide-pointer-focus', !keyboard);
      this.previousOverflow = document.body.style.overflow;
      this.modal.showModal();
      document.body.style.overflow = 'hidden';
      this.locked = true;
      this.modal.querySelector('[data-size-guide-close]')?.focus();
    }
    restore() {
      if (!this.locked) return;
      document.body.style.overflow = this.previousOverflow;
      this.locked = false;
      if (this.opener?.isConnected) { this.opener.toggleAttribute('data-guide-pointer-focus', !this.keyboardOpen); this.opener.focus({ preventScroll: true }); }
    }
  }
  customElements.define('monateo-size-picker', MonateoSizePicker);
})();
